import React from "react";
import { call } from "ionicons/icons";
import CustomItem from "./CustomItem";
import CustomBox from "./CustomBox";
import CustomMultiColView from "./CustomMultiColView";
import { Project } from "../models/Project";
import moment from "moment";
import { EmergencyContact } from "../models/EmergencyContact";
import { CalibrationCertificate } from "../models/CalibrationCertificate";
import { PermitTemplate } from "../models/PermitTemplate";
import { PermitFieldValue } from "../models/PermitFieldValue";

interface Props {
    locationOfWork: string;
    validFrom: Date;
    validTo: Date;
    comments: string;
    emergencyContact?: EmergencyContact;
    submittedBy: string;
    dateSubmitted?: Date;
    workDescription: string;
    maxPenetrationDepth: string;
    permittedEquipment: string;
    subContractor: string;
    project: Project;
    calibrationCertificate?: CalibrationCertificate;
    template?: PermitTemplate;
    fieldValues?: PermitFieldValue[];
}

const PermitDetails: React.FC<Props> = ({
    locationOfWork,
    validFrom,
    validTo,
    comments,
    emergencyContact,
    submittedBy,
    dateSubmitted,
    workDescription,
    maxPenetrationDepth,
    permittedEquipment,
    subContractor,
    project,
    calibrationCertificate,
    template,
    fieldValues,
}) => {
    const permitValid = `${moment(validFrom).format("L")} - ${moment(validTo).format("L")}`;
    const submission = `${submittedBy} at ${moment(dateSubmitted).format("lll")}`;

    const orderedFields = template?.fields?.slice().sort((a, b) => a.displayOrder - b.displayOrder);

    const showLegacyFields = !orderedFields?.length && (maxPenetrationDepth != null || permittedEquipment != null);

    return (
        <>
            <CustomBox title="Basic details">
                <CustomMultiColView>
                    <CustomItem title="Location of work" text={locationOfWork}></CustomItem>
                    <CustomItem title="Permit valid" text={permitValid}></CustomItem>
                    <CustomItem title="Authorised by" text={submission}></CustomItem>
                    <CustomItem title="Sub-contractor" text={subContractor}></CustomItem>
                    <CustomItem title="Works description" text={workDescription}></CustomItem>
                    {orderedFields?.map((field) => {
                        const fv = fieldValues?.find((v) => v.fieldKey === field.key);
                        const displayValue =
                            field.fieldType === "Date" ? (fv?.displayValue ? moment(fv.displayValue).format("L") : "") : fv?.displayValue ?? "";
                        return <CustomItem key={field.key} title={field.label} text={displayValue} />;
                    })}
                    {showLegacyFields && (
                        <>
                            <CustomItem title="Max penetration depth" text={maxPenetrationDepth}></CustomItem>
                            <CustomItem title="Permitted equipment" text={permittedEquipment}></CustomItem>
                        </>
                    )}
                    {(template?.features.catAndGenny?.enabled ?? true) && (
                        <>
                            <CustomItem
                                title="CAT serial number"
                                text={calibrationCertificate?.catSerialNumber}
                            ></CustomItem>
                            <CustomItem
                                title="CAT calibration date"
                                text={
                                    (calibrationCertificate?.catCalibrationDate
                                        ? moment(calibrationCertificate.catCalibrationDate).format("L") + " "
                                        : "") +
                                    (calibrationCertificate?.catCalibrationDueDate
                                        ? `due ${moment(calibrationCertificate.catCalibrationDueDate).format("L")}`
                                        : "")
                                }
                            ></CustomItem>
                            <CustomItem
                                title="Genny serial number"
                                text={calibrationCertificate?.gennySerialNumber}
                            ></CustomItem>
                            <CustomItem
                                title="Genny calibration date"
                                lines="none"
                                text={
                                    (calibrationCertificate?.gennyCalibrationDate
                                        ? moment(calibrationCertificate.gennyCalibrationDate).format("L") + " "
                                        : "") +
                                    (calibrationCertificate?.gennyCalibrationDueDate
                                        ? `due ${moment(calibrationCertificate.gennyCalibrationDueDate).format("L")}`
                                        : "")
                                }
                            ></CustomItem>
                        </>
                    )}
                </CustomMultiColView>
            </CustomBox>
            <CustomBox title="Comments">
                <div style={{ padding: 16 }}>{comments}</div>
            </CustomBox>
            <CustomBox title="Project">
                <CustomMultiColView>
                    <CustomItem title="Project name" text={project.name}></CustomItem>
                    <CustomItem title="Project number" text={project.number?.toString()}></CustomItem>
                    {emergencyContact && emergencyContact.name !== "" ? (
                        <CustomItem
                            title="Emergency contact"
                            text={emergencyContact.name}
                            tagIcon={call}
                            tag={emergencyContact.phone}
                            lines="none"
                        ></CustomItem>
                    ) : (
                        <CustomItem title="Emergency contact" text="-" lines="none"></CustomItem>
                    )}
                </CustomMultiColView>
            </CustomBox>
        </>
    );
};

export default PermitDetails;
